<template src="./keyword-dedup-detail.component.html"></template>
<style scoped src="./keyword-dedup-detail.component.css"></style>
<script lang="ts">
import Vue from 'vue';
import axios from 'axios';
import Modal from '@/components/modal/modal.component.vue';
import Sidebar from '@/components/sidebar/sidebar.component.vue';
import api from '@/api';
import {getErrorMessage, noop} from '@/helpers';
import {toasted} from '@/plugins/toasted';
import type {KeywordDedupDetail} from '@/models';

type KeywordDedupDetailRow = {
  label: string
  display: string
  isJson: boolean
};

type ParseJsonRowConfig = {
  label: string
  path: string[]
};

function getQueryParamValue(query: Record<string, any>, key: string) {
  const value = query[key];
  if (Array.isArray(value)) return String(value[0] ?? '');
  return typeof value === 'string' ? value : '';
}

const PARSE_JSON_ROW_CONFIG: ParseJsonRowConfig[] = [{
  label: 'parse_json.language',
  path: ['language'],
}, {
  label: 'parse_json.market',
  path: ['market'],
}, {
  label: 'parse_json.query_type',
  path: ['query_type'],
}, {
  label: 'parse_json.base_entity',
  path: ['base_entity'],
}, {
  label: 'parse_json.canonical_candidate',
  path: ['canonical_candidate'],
}, {
  label: 'parse_json.keep_attributes.size',
  path: ['keep_attributes', 'size'],
}, {
  label: 'parse_json.keep_attributes.year',
  path: ['keep_attributes', 'year'],
}, {
  label: 'parse_json.keep_attributes.model',
  path: ['keep_attributes', 'model'],
}, {
  label: 'parse_json.keep_attributes.trim',
  path: ['keep_attributes', 'trim'],
}, {
  label: 'parse_json.keep_attributes.color',
  path: ['keep_attributes', 'color'],
}, {
  label: 'parse_json.keep_attributes.material',
  path: ['keep_attributes', 'material'],
}, {
  label: 'parse_json.keep_attributes.style',
  path: ['keep_attributes', 'style'],
}, {
  label: 'parse_json.keep_attributes.condition',
  path: ['keep_attributes', 'condition'],
}, {
  label: 'parse_json.keep_attributes.retailer_marketplace_payment',
  path: ['keep_attributes', 'retailer_marketplace_payment'],
}, {
  label: 'parse_json.keep_attributes.audience',
  path: ['keep_attributes', 'audience'],
}, {
  label: 'parse_json.keep_attributes.segment',
  path: ['keep_attributes', 'segment'],
}, {
  label: 'parse_json.keep_attributes.travel_geo',
  path: ['keep_attributes', 'travel_geo'],
}, {
  label: 'parse_json.keep_attributes.price_constraint',
  path: ['keep_attributes', 'price_constraint'],
}, {
  label: 'parse_json.keep_attributes.use_case',
  path: ['keep_attributes', 'use_case'],
}, {
  label: 'parse_json.drop_modifiers',
  path: ['drop_modifiers'],
}, {
  label: 'parse_json.relations',
  path: ['relations'],
}, {
  label: 'parse_json.entities',
  path: ['entities'],
}, {
  label: 'parse_json.is_comparison',
  path: ['is_comparison'],
}, {
  label: 'parse_json.is_multi_entity',
  path: ['is_multi_entity'],
}, {
  label: 'parse_json.is_accessory_or_relation_query',
  path: ['is_accessory_or_relation_query'],
}, {
  label: 'parse_json.is_service_or_store_query',
  path: ['is_service_or_store_query'],
}, {
  label: 'parse_json.parse_confidence',
  path: ['parse_confidence'],
}, {
  label: 'parse_json.notes',
  path: ['notes'],
}];

const INLINE_ARRAY_LABELS = new Set([
  'parse_json.keep_attributes.model',
  'parse_json.keep_attributes.condition',
  'parse_json.keep_attributes.travel_geo',
  'parse_json.drop_modifiers',
  'parse_json.entities',
]);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function hasMeaningfulValue(value: unknown) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (isPlainObject(value)) return Object.keys(value).length > 0;
  return true;
}

function capitalizeFirstLetter(value: unknown) {
  const normalized = String(value ?? '').trim();
  if (normalized.length === 0) return '-';
  return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`;
}

function getNestedValue(source: unknown, path: string[]) {
  let current = source;
  for (const key of path) {
    if (!isPlainObject(current)) return undefined;
    current = current[key];
  }
  return current;
}

export default Vue.extend({
  components: {
    Modal,
    Sidebar,
  },
  data() {
    return {
      ac: null as AbortController | null,
      detail: null as KeywordDedupDetail | null,
      detailError: '',
      isDeleting: false,
      showLoading: false,
      showDeleteModal: false,
    };
  },
  computed: {
    pageHeading(): string {
      if (!this.detail?.keyword) return 'Keyword Deduplication';
      return `Keyword Deduplication for ${this.detail.keyword}`;
    },
    canCopyRawParseJson(): boolean {
      return Boolean(this.detail && this.detail.parse_json !== null && this.detail.parse_json !== undefined);
    },
    detailRows(): KeywordDedupDetailRow[] {
      if (!this.detail) return [];

      const rows: KeywordDedupDetailRow[] = [{
        label: 'keyword',
        display: this.formatRequiredValue(this.detail.keyword),
        isJson: false,
      }, {
        label: 'market',
        display: this.formatRequiredValue(this.detail.market),
        isJson: false,
      }, {
        label: 'vertical',
        display: this.formatRequiredValue(this.detail.vertical),
        isJson: false,
      }, {
        label: 'normalized_surface',
        display: this.formatRequiredValue(this.detail.normalized_surface),
        isJson: false,
      }, {
        label: 'query_type',
        display: capitalizeFirstLetter(this.detail.query_type),
        isJson: false,
      }, {
        label: 'base_entity',
        display: this.formatRequiredValue(this.detail.base_entity),
        isJson: false,
      }, {
        label: 'canonical_candidate',
        display: this.formatRequiredValue(this.detail.canonical_candidate),
        isJson: false,
      }, {
        label: 'cluster_id',
        display: this.formatRequiredValue(this.detail.cluster_id),
        isJson: false,
      }, {
        label: 'created_at',
        display: this.formatRequiredValue(this.detail.created_at),
        isJson: false,
      }];

      const parseJson = this.detail.parse_json;
      for (const item of PARSE_JSON_ROW_CONFIG) {
        const row = this.createOptionalRow(item.label, getNestedValue(parseJson, item.path));
        if (row) rows.push(row);
      }

      if (isPlainObject(parseJson) && Array.isArray(parseJson.alias_normalizations)) {
        parseJson.alias_normalizations.forEach((entry, index) => {
          const alias = isPlainObject(entry) ? entry.alias : undefined;
          const canonicalForm = isPlainObject(entry) ? entry.canonical_form : undefined;
          const aliasRow = this.createOptionalRow(`parse_json.alias_normalizations[${index}].alias`, alias);
          if (aliasRow) rows.push(aliasRow);
          const canonicalFormRow = this.createOptionalRow(`parse_json.alias_normalizations[${index}].canonical_form`, canonicalForm);
          if (canonicalFormRow) rows.push(canonicalFormRow);
        });
      }

      return rows;
    },
    listQuery(): Record<string, string> {
      const query: Record<string, string> = {};
      const helperKeys = new Set(['canonical', 'sourceMarket', 'returnClusterId']);
      for (const [key, value] of Object.entries(this.$route.query)) {
        if (helperKeys.has(key)) continue;
        if (Array.isArray(value)) {
          if (value[0] !== undefined) query[key] = String(value[0]);
          continue;
        }
        if (value !== undefined && value !== null) {
          query[key] = String(value);
        }
      }
      return query;
    },
    backRoute(): Record<string, any> {
      const returnClusterId = getQueryParamValue(this.$route.query, 'returnClusterId').trim();
      if (returnClusterId) {
        const query = {...this.listQuery};
        const canonical = getQueryParamValue(this.$route.query, 'canonical').trim();
        const sourceMarket = getQueryParamValue(this.$route.query, 'sourceMarket').trim();
        if (canonical) query.canonical = canonical;
        if (sourceMarket) query.sourceMarket = sourceMarket;
        return {
          name: 'KeywordDedupCanonicalDetail',
          params: {clusterId: returnClusterId},
          query,
        };
      }
      return {
        name: 'KeywordDedupList',
        query: this.listQuery,
      };
    },
    rawParseJson(): string {
      if (!this.detail || this.detail.parse_json === null || this.detail.parse_json === undefined) {
        return 'No parse_json available';
      }
      return JSON.stringify(this.detail.parse_json, null, 2) ?? 'No parse_json available';
    },
  },
  watch: {
    '$route.params.id': {
      async handler() {
        await this.getKeywordDetail();
      },
      immediate: true,
    },
    'detail': {
      handler() {
        document.title = `${this.pageHeading} | Revenuedriver S.R.L`;
      },
      immediate: true,
    },
  },
  mounted() {
    document.body.classList.add(
      'min-w-site',
      'bg-40',
      'text-90',
      'font-medium',
      'min-h-full',
    );
  },
  destroyed() {
    document.body.classList.remove(
      'min-w-site',
      'bg-40',
      'text-90',
      'font-medium',
      'min-h-full',
    );
    this.ac?.abort();
  },
  methods: {
    openDeleteModal() {
      if (!this.detail || this.showLoading || this.isDeleting) return;
      this.showDeleteModal = true;
    },
    closeDeleteModal() {
      if (this.isDeleting) return;
      this.showDeleteModal = false;
    },
    async deleteKeyword() {
      const id = String(this.$route.params.id ?? '').trim();
      if (!id) {
        toasted.showErrorMessage('Missing keyword id.');
        return;
      }

      try {
        this.isDeleting = true;
        await api.execute('delete', `/keyword-dedup/keywords/${encodeURIComponent(id)}`);
        this.showDeleteModal = false;
        toasted.showSuccessMessage('Dedup keyword deleted');
        await this.$router.push(this.backRoute).catch(noop);
      } catch (e) {
        console.error(e);
        toasted.showErrorMessage(getErrorMessage(e));
      } finally {
        this.isDeleting = false;
      }
    },
    async copyRawParseJson() {
      if (!this.canCopyRawParseJson) {
        toasted.showErrorMessage('No parse_json available to copy');
        return;
      }

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(this.rawParseJson);
        } else {
          const textarea = document.createElement('textarea');
          textarea.value = this.rawParseJson;
          textarea.setAttribute('readonly', '');
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          textarea.style.pointerEvents = 'none';
          document.body.appendChild(textarea);
          textarea.focus();
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }
        toasted.showSuccessMessage('Raw parse_json copied');
      } catch (e) {
        console.error(e);
        toasted.showErrorMessage('Unable to copy raw parse_json');
      }
    },
    async getKeywordDetail() {
      const id = String(this.$route.params.id ?? '').trim();
      if (!id) {
        this.detail = null;
        this.detailError = 'Missing keyword id.';
        return;
      }

      try {
        this.showLoading = true;
        this.detailError = '';
        this.ac?.abort();
        this.ac = new AbortController();
        this.detail = await api.execute(
          'get',
          `/keyword-dedup/keywords/${encodeURIComponent(id)}`,
          null,
          null,
          this.ac.signal,
        ) as KeywordDedupDetail;
      } catch (e) {
        console.error(e);
        if (!axios.isCancel(e)) {
          this.detail = null;
          this.detailError = getErrorMessage(e);
          toasted.showErrorMessage(this.detailError);
        }
      } finally {
        this.showLoading = false;
      }
    },
    formatValue(value: unknown) {
      if (value === null || value === undefined) return '-';
      if (Array.isArray(value) || isPlainObject(value)) {
        return JSON.stringify(value, null, 2) ?? '-';
      }
      return String(value);
    },
    formatRequiredValue(value: unknown) {
      return this.formatValue(value);
    },
    formatInlineArray(value: unknown[]) {
      return value
        .map(entry => {
          if (entry === null || entry === undefined) return '';
          if (Array.isArray(entry) || isPlainObject(entry)) {
            return JSON.stringify(entry) ?? '';
          }
          return String(entry).trim();
        })
        .filter(Boolean)
        .join(', ');
    },
    createOptionalRow(label: string, value: unknown): KeywordDedupDetailRow | null {
      if (!hasMeaningfulValue(value)) return null;

      if (INLINE_ARRAY_LABELS.has(label) && Array.isArray(value)) {
        const display = this.formatInlineArray(value);
        if (!display) return null;
        return {
          label,
          display,
          isJson: false,
        };
      }

      return {
        label,
        display: this.formatValue(value),
        isJson: Array.isArray(value) || isPlainObject(value),
      };
    },
  },
});
</script>
